"use client";

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged, signInWithCustomToken, User } from 'firebase/auth';
import { auth, db } from '@/lib/firebase/client';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { fnUrl } from '@/lib/functionsBase';

interface UserData {
  uid: string;
  email: string | null;
  role: 'client' | 'owner' | 'admin' | null;
  businessId?: string;
  name?: string;
  phone?: string;
  /** UI language the user last used — the backend sends push/WhatsApp in it. */
  lang?: string;
  likedArticles?: string[];
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  activeRole: 'client' | 'owner' | 'admin' | null;
  setActiveRole: (role: 'client' | 'owner' | 'admin') => void;
  toggleLike: (articleId: string) => Promise<void>;
}

const SUPPORTED_LANGS = ['he', 'ru', 'en', 'fr'];

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  loading: true,
  activeRole: null,
  setActiveRole: () => {},
  toggleLike: async () => {},
});

/**
 * Check URL for __authToken parameter (passed from native app via custom token).
 * If found, use signInWithCustomToken to authenticate, then clean up the URL.
 */
async function handleNativeAuthToken(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('__authToken');

    if (!authToken) return false;

    console.log('[AuthContext] Native auth token detected, signing in...');
    await signInWithCustomToken(auth, authToken);

    // Clean up URL — remove the token parameter
    params.delete('__authToken');
    const cleanSearch = params.toString();
    const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
    window.history.replaceState({}, '', cleanUrl);

    console.log('[AuthContext] Native auth sync successful');
    return true;
  } catch (error) {
    console.error('[AuthContext] Failed to sign in with custom token:', error);
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [activeRole, setActiveRole] = useState<'client' | 'owner' | 'admin' | null>(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  // Last language written to Firestore — keeps the sync effect to one write.
  const langSyncedRef = useRef<string | null>(null);

  // Handle native auth token on mount (before onAuthStateChanged fires)
  useEffect(() => {
    handleNativeAuthToken();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Ensure the auth ID token is minted/attached BEFORE any Firestore read
      // fires (here or in downstream pages), so reads don't race the token and
      // hit "Missing or insufficient permissions" — the main reason data fails
      // to load on a cold start inside the app WebView (no offline cache yet).
      if (firebaseUser) {
        try { await firebaseUser.getIdToken(); } catch { /* proceed regardless */ }
      }
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Fetch custom user data from Firestore
        try {
          // FCM push token — deferred OFF the cold-start critical path so the
          // dynamic import never delays role resolution / first nav paint.
          // Fire-and-forget once the browser is idle (or after a short delay).
          const initFcm = () => {
            import('@/lib/firebase/messaging')
              .then(({ requestForToken }) => requestForToken(firebaseUser.uid))
              .catch((fcmErr) => console.error('Failed to init FCM:', fcmErr));
          };
          if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(initFcm);
          } else if (typeof window !== 'undefined') {
            setTimeout(initFcm, 2000);
          }

          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          let data: UserData;
          if (userDoc.exists()) {
            data = userDoc.data() as UserData;
          } else {
            console.log("No user document found, defaulting to client");
            data = { uid: firebaseUser.uid, email: firebaseUser.email, role: 'client' };
          }
          // Link this account to any past bookings made with the same VERIFIED
          // email or phone. Done server-side (Admin SDK) — the client can't query
          // users/appointments by email/phone under our rules. linkMyBookings
          // re-points those appointments' userId to this uid so "My bookings"
          // shows the client's full history after a Google/phone login.
          // Best-effort and non-blocking: never delays or breaks login.
          try {
            const linkTok = await firebaseUser.getIdToken();
            fetch(fnUrl('linkMyBookings'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ idToken: linkTok }),
            }).catch(() => {});
          } catch { /* best-effort link, never blocks login */ }

          // HARDCODE OVERRIDE FOR ADMIN
          if (firebaseUser.email?.toLowerCase() === 'zkorostik@gmail.com' && data.role !== 'admin') {
            data.role = 'admin';
            if (userDoc.exists()) {
              updateDoc(userDocRef, { role: 'admin' }).catch(console.error);
            } else {
              // Note: the setDoc for new users usually happens elsewhere, but we do updateDoc here.
              // If it's totally new, it's safer to just setDoc.
              const { setDoc } = await import('firebase/firestore');
              setDoc(userDocRef, data, { merge: true }).catch(console.error);
            }
          }

          // Auto-detect business ownership: if role is 'client', check if user owns a business
          if (data.role === 'client' || !data.role) {
            // Claim a profile transferred to this user's e-mail (admin set claimEmail).
            // Works with ANY login (Google/Apple/email): the server migrates the
            // profile to businesses/{uid} so the master instantly owns it.
            try {
              const idTok = await firebaseUser.getIdToken();
              const cr = await fetch(fnUrl('claimBusinessByEmail'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: idTok }),
              });
              const cj = await cr.json().catch(() => ({}));
              if (cj?.claimed && cj.businessId) {
                data = { ...data, role: 'owner', businessId: cj.businessId };
              }
            } catch { /* best-effort claim, never blocks login */ }
            try {
              const bizQuery = query(
                collection(db, 'businesses'),
                where('ownerId', '==', firebaseUser.uid)
              );
              const bizSnap = await getDocs(bizQuery);
              if (!bizSnap.empty) {
                // User owns a business — auto-upgrade to 'owner'
                const businessDoc = bizSnap.docs[0];
                data = { ...data, role: 'owner', businessId: businessDoc.id };
                // Also update the users doc so next load is faster
                if (userDoc.exists()) {
                  updateDoc(userDocRef, { role: 'owner', businessId: businessDoc.id }).catch(console.error);
                }
              }
            } catch (bizErr) {
              console.error("Error checking business ownership:", bizErr);
            }
          }

          setUserData(data);
            
          // Try to restore localized activeRole from localStorage if it's permitted for this user
          const savedRole = localStorage.getItem('activeRole') as 'client' | 'owner' | 'admin' | null;
          if (data.role === 'admin') {
            setActiveRole(savedRole || 'admin');
          } else if (data.role === 'owner') {
            setActiveRole(savedRole === 'client' ? 'client' : 'owner');
          } else {
            setActiveRole('client');
          }
        } catch (error) {
          console.error("Error fetching user data", error);
        }
      } else {
        setUserData(null);
        setActiveRole(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSetActiveRole = (role: 'client' | 'owner' | 'admin') => {
    if (!userData) return;
    // Admins can be anything
    if (userData.role === 'admin') {
      setActiveRole(role);
      localStorage.setItem('activeRole', role);
    }
    // Owners can be clients or owners
    else if (userData.role === 'owner') {
      const allowed = role === 'owner' ? 'owner' : 'client';
      setActiveRole(allowed);
      localStorage.setItem('activeRole', allowed);
    }
    // Clients can only be clients
    else {
      setActiveRole('client');
      localStorage.setItem('activeRole', 'client');
    }
  };
  const toggleLike = async (articleId: string) => {
    if (!user || !userData) return;
    const currentLikes = userData.likedArticles || [];
    const isLiked = currentLikes.includes(articleId);
    let newLikes: string[];
    
    if (isLiked) {
      newLikes = currentLikes.filter(id => id !== articleId);
    } else {
      newLikes = [...currentLikes, articleId];
    }
    
    // Optimistic update
    setUserData({ ...userData, likedArticles: newLikes });
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { likedArticles: newLikes });
    } catch (err) {
      console.error('Failed to update likes', err);
      // Revert on error
      setUserData({ ...userData, likedArticles: currentLikes });
    }
  };

  // Remember the language the user actually reads the app in. The backend has
  // no other signal for an app-only client (the WhatsApp session language only
  // exists for people who wrote to the bot), so without this every push and
  // reminder went out in Hebrew regardless of the app's language.
  useEffect(() => {
    const lang = i18n.language;
    if (!user || !SUPPORTED_LANGS.includes(lang)) return;
    if (langSyncedRef.current === lang || userData?.lang === lang) return;
    langSyncedRef.current = lang;
    setDoc(doc(db, 'users', user.uid), { lang }, { merge: true })
      .catch((err) => console.error('Failed to save language preference', err));
  }, [user, userData?.lang, i18n.language]);

  // Bridge the resolved role → native app shell (React Native WebView) so the
  // native bottom tab bar can render role-aware tabs. No-op on plain web.
  useEffect(() => {
    const rn = (window as unknown as { ReactNativeWebView?: { postMessage: (m: string) => void } }).ReactNativeWebView;
    if (!rn) return;
    try {
      rn.postMessage(JSON.stringify({ type: 'YOFI_CTX', role: userData?.role ?? null, activeRole, loggedIn: !!user }));
    } catch { /* ignore bridge errors */ }
  }, [userData?.role, activeRole, user]);

  return (
    <AuthContext.Provider value={{ user, userData, loading, activeRole, setActiveRole: handleSetActiveRole, toggleLike }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
